import { useMemo } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import type { ConsensusScore, Participant } from '../types';

interface ConsensusViewProps {
  participants: Participant[];
  consensusRankings: ConsensusScore[];
  items: Array<{ id: string; name: string }>;
}

export function ConsensusView({
  participants,
  consensusRankings,
  items,
}: ConsensusViewProps) {
  // Calculate overall consensus (average of all item consensus scores)
  const overallConsensus = useMemo(() => {
    if (consensusRankings.length === 0) return 0;
    const sum = consensusRankings.reduce(
      (acc, item) => acc + item.consensusScore,
      0
    );
    return sum / consensusRankings.length;
  }, [consensusRankings]);

  // Get consensus level label
  const getConsensusLabel = (score: number) => {
    if (score >= 0.8) return { label: 'Strong Consensus', color: 'text-[var(--green)] dark:text-[var(--text-ter)]' };
    if (score >= 0.6) return { label: 'Moderate Consensus', color: 'text-[var(--text-ter)]' };
    if (score >= 0.4) return { label: 'Weak Consensus', color: 'text-[var(--mustard)]' };
    return { label: 'Low Consensus', color: 'text-[var(--coral)]' };
  };

  // Get color for consensus score
  const getConsensusColor = (score: number) => {
    if (score >= 0.8) return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]';
    if (score >= 0.6) return 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]';
    if (score >= 0.4) return 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]';
    return 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]';
  };

  const overallLabel = getConsensusLabel(overallConsensus);

  return (
    <div className="space-y-6">
      {/* Overall Consensus Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border border-[var(--slate)]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-pri)]">
              Overall Consensus
            </h3>
            <p className="text-sm text-[var(--text-sec)] mt-1">
              {participants.length} participant{participants.length !== 1 ? 's' : ''} ranked{' '}
              {items.length} items
            </p>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${overallLabel.color}`}>
              {(overallConsensus * 100).toFixed(0)}%
            </div>
            <div className={`text-sm font-medium ${overallLabel.color}`}>
              {overallLabel.label}
            </div>
          </div>
        </div>
      </div>

      {/* Consensus Rankings */}
      <div>
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
          Consensus Rankings
        </h3>
        <div className="space-y-3">
          {consensusRankings.map((item, index) => {
            const consensusLabel = getConsensusLabel(item.consensusScore);
            const rankChange = index + 1 - item.averageRank;
            
            return (
              <div
                key={item.itemId}
                className={`p-4 border  ${getConsensusColor(item.consensusScore)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--bg-raised)] border-2 border-current flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-semibold">{item.itemName}</div>
                        <div className="text-sm mt-1 opacity-80">
                          Avg Rank: {item.averageRank.toFixed(1)} • Borda: {item.bordaScore.toFixed(0)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Consensus Score */}
                    <div className="text-right">
                      <div className="text-sm font-medium opacity-80">
                        Consensus
                      </div>
                      <div className="text-lg font-bold">
                        {(item.consensusScore * 100).toFixed(0)}%
                      </div>
                      <div className="text-xs opacity-70">
                        {consensusLabel.label}
                      </div>
                    </div>

                    {/* Rank vs Average Indicator */}
                    {Math.abs(rankChange) > 0.5 && (
                      <div className="flex items-center gap-1">
                        {rankChange > 0 ? (
                          <TrendingUp className="h-5 w-5 text-[var(--green)]" />
                        ) : (
                          <TrendingDown className="h-5 w-5 text-[var(--coral)]" />
                        )}
                        <span className="text-sm font-medium">
                          {Math.abs(rankChange).toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Standard Deviation Bar */}
                <div className="mt-3 pt-3 border-t border-current border-opacity-20">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span>Rank Variance</span>
                    <span>σ = {item.standardDeviation.toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-current bg-opacity-10 rounded-full h-2">
                    <div
                      className="bg-current h-2 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, (item.standardDeviation / 5) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


